import {
  OverflowReporter,
  ToReport
} from '../../common/Reporter/report/interfaces';
import DataReportBlock from './DataReportBlock';

export type StringRetreiver = () => string;

export const getKey = (data: unknown): string => {
  const item = data as Record<string, string | StringRetreiver>;
  if (typeof item.key === 'string') {
    return item.key;
  }
  if (typeof item.key === 'function') {
    return item.key();
  }
  return JSON.stringify(data);
};

export interface GetDataReportMapArgs<In, Storage extends ToReport<Out>, Out> {
  max: number;
  name: string;
  overflowReporter: OverflowReporter;
  toStorage?: (obj: In) => Storage;
}

export const getDataReportMap = <In, Storage extends ToReport<Out>, Out>(
  obj: GetDataReportMapArgs<In, Storage, Out>
): DataReportMap<In, Storage, Out> => {
  return new DataReportMap<In, Storage, Out>({
    max: obj.max,
    name: obj.name,
    overflowReporter: obj.overflowReporter,
    toStorage: obj.toStorage
  });
};

export interface DataReportMapInterface<In, Storage> {
  data?: Record<string, Storage>; // Item being stored
  max: number; // Max number of items to store
  name: string; // Name of the item (for reporting overages)
  // Function to tell when we have maxed out our capacity.
  overflowReporter: OverflowReporter;
  // Function to change an item into a Storage item.
  toStorage?: (item: In) => Storage;
}

export default class DataReportMap<In, Storage extends ToReport<Out>, Out>
  implements DataReportMapInterface<In, Storage> {
  data: Record<string, Storage>;
  max: number;
  name: string;
  overflowReporter: OverflowReporter;
  toStorage?: (item: In) => Storage;

  constructor(obj: DataReportMapInterface<In, Storage>) {
    this.data = obj.data || {};
    this.max = obj.max;
    this.name = obj.name;
    this.overflowReporter = obj.overflowReporter;
    this.toStorage = obj.toStorage;
  }

  append(item: Storage | In): void {
    if (!item) {
      return;
    }
    const data = this.toStorage
      ? this.toStorage(item as In)
      : (item as Storage);
    // SRV-1703: Make sure that toReport is defined.
    const asRsu = (data as unknown) as Record<string, unknown>;
    if (typeof asRsu.toReport !== 'function') {
      asRsu.toReport = () => (data as unknown) as Out;
    }
    const key = getKey(data);
    const obj = this.data[key];
    if (!obj) {
      if (this.max && Object.keys(this.data).length >= this.max) {
        if (this.overflowReporter) {
          this.overflowReporter(this.name);
        }
      } else {
        this.data[key] = data;
      }
    } else if (obj.append) {
      obj.append(data);
    }
  }

  toReport(): DataReportBlock<Out> {
    const data = Object.values(this.data).map(v => v.toReport && v.toReport());
    return {
      data: data.filter(d => typeof d !== 'undefined')
    };
  }
}
